import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { getViteApiUrl } from '../config/env';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [step, setStep] = useState<'login' | '2fa'>('login');
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    // Forzar la animación cada vez que se monta el componente
    setAnimationKey(prev => prev + 1);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${getViteApiUrl()}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error al iniciar sesión' }));
        throw new Error(errorData.message || 'Credenciales inválidas');
      }

      const data = await response.json();

      if (data.requiresTwoFactor && data.twoFactorToken) {
        setStep('2fa');
        setTwoFactorToken(data.twoFactorToken);
        setError(null);
        setLoading(false);
        return;
      }

      // Limpia caché de la sesión anterior para evitar respuestas mezcladas
      // (por ejemplo, /auth/me de una sesión previa que se pintea en otra ruta).
      queryClient.clear();
      login(data.user);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handle2faSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorToken || !twoFactorCode.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const response = await fetch(`${getViteApiUrl()}/auth/2fa/complete-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ twoFactorToken, code: twoFactorCode.trim() }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Código incorrecto' }));
        throw new Error(err.message || 'Código incorrecto');
      }
      const data = await response.json();
      // Limpia caché para evitar estados/respuestas de otra sesión
      queryClient.clear();
      login(data.user);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a1f3a] via-[#25326B] to-[#1a1f3a] p-4">
      <div 
        key={animationKey}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 animate-slide-up-bounce"
      >
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img 
            src="/images/logo.png" 
            alt="JNU Group Logo" 
            className="h-16 w-auto object-contain"
            onError={(e) => {
              // Fallback si la imagen no existe
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <p className="text-sm text-gray-600">Sistema de Gestión de Inversiones</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {step === '2fa' ? (
          <form onSubmit={handle2faSubmit} className="space-y-6">
            <p className="text-sm text-gray-600 text-center">
              Ingresa el código de 6 dígitos de tu app de autenticación
            </p>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Código
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="block w-full px-4 py-3 text-center text-lg tracking-widest bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent"
                style={{ colorScheme: 'light' }}
              />
            </div>
            <button
              type="submit"
              disabled={loading || twoFactorCode.length !== 6}
              className="w-full bg-[#FA4999] hover:bg-[#FA4999]/90 text-white font-medium py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('login'); setTwoFactorCode(''); setTwoFactorToken(null); setError(null); }}
              className="w-full text-gray-600 hover:text-gray-800 text-sm"
            >
              ← Volver al inicio de sesión
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="block w-full pl-10 pr-3 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent outline-none transition-all placeholder-gray-400"
                style={{ colorScheme: 'light' }}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="block w-full pl-10 pr-12 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent outline-none transition-all placeholder-gray-400"
                style={{ colorScheme: 'light' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FA4999] hover:bg-[#FA4999]/90 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span>Cargando...</span>
            ) : (
              <>
                <span>Iniciar Sesión</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-gray-600 hover:text-[#25326B] hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

